/**
 * My Money Meetings' v2 view (TMMAPP-0151 §I 25–26; prototype state 43, P:663–702): the top of
 * `/me/meetings`, above the existing tabs.
 *
 * - **The next meeting bar:** the first SCHEDULED occurrence ahead in `GET /v1/me/meetings` (the page's
 *   own read), a warm callout (`text` at 14 on the peach soft) "Next: {title} · {date} · {time}" with
 *   **Prepare**, which opens
 *   that occurrence's session (TMMAPP-0159 §B 6). Left out when nothing is scheduled.
 * - **Four cards** (two across at `lg`, one below), each left out when it has no rows, from one
 *   `GET /v1/me/items` read (every status) and the page's past meetings:
 *   - **Commitments** (OPEN and DONE): a checkbox, the text, "{owner or Everyone} · due {date} ·
 *     {envelope}". A writer's tick opens a short reason prompt prefilled "Done" (MM-D-64), then
 *     `PATCH {status: DONE, reason}`; an untick is the same, prefilled "Reopened", to OPEN. Anyone
 *     else sees the box, not a control.
 *   - **Decisions** (AGREED and REVISED): the status chip (Revised peach, Agreed sage), the text, and
 *     "v{version} · {actor} · {date} · {envelope}" from the served `lastActorDisplayName` and
 *     `envelopeName` (TMMAPI-0186 U3), whatever of it is served.
 *   - **What you're working toward** (OPEN goals): the text, a bar and "{balance} of {target} ·
 *     {pct}" from the served `progressMinor` and `targetMinor`, in the virtual account's currency.
 *   - **Past meetings:** the latest three, "{date} · {virtual account}", the title, the served note
 *     excerpt.
 *
 *   An item's text opens its meeting session (a goal with an envelope opens the envelope); a past
 *   meeting opens its session.
 *
 * A standalone meeting's item (TMMAPP-0159 §D 24, no virtual account) reads "With {names}" where an
 * item names its envelope, from the page's own meeting rows.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import {
  meetingsApi,
  type Meeting,
  type MeetingCalendarEntry,
  type MeetingItem,
} from '@/api/meetings';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { ErrorState } from '@/components/ErrorState';
import { GuardedDialog } from '@/components/GuardedDialog';
import { MoneyParts } from '@/components/MoneyText';
import { ProgressTrack } from '@/components/ProgressTrack';
import { CheckIcon, MeetingsIcon } from '@/components/icons';
import { statusLabel } from '@/components/statusLabel';
import { SubmitButton } from '@/components/SubmitButton';
import { TintChip } from '@/components/TintChip';
import { formatCivilDateNear, formatInstant, formatMoneyMinor, formatPercent } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone, utcToZonedCivil } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { useNotify } from '@/shell/NotifyProvider';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { tintFor } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';
import { canWriteNotes } from './canWriteNotes';
import { meetingSessionPath } from './nextMeeting';
import {
  canWriteStandalone,
  entryWithLabel,
  meetingWithLabel,
  organizesEntry,
  sessionPathOf as sessionPath,
} from './standalone';

const PAST_SHOWN = 3;
const REASON_MAX = 500;

/** A content card (visual contract v2 §6): its `h2` in the heading face, then its rows. */
function OverviewCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Paper
      component="section"
      aria-label={title}
      data-meetings-card=""
      sx={{ p: { xs: '18px 16px', sm: '20px 22px' }, display: 'flex', flexDirection: 'column' }}
    >
      <Typography variant="h3" component="h2" sx={{ fontSize: 23, lineHeight: 1.2, mb: '6px' }}>
        {title}
      </Typography>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {children}
      </Box>
    </Paper>
  );
}

const ROW_SX = { borderTop: '1px solid', py: '12px' } as const;

/** The reason prompt a commitment's tick or untick asks for (MM-D-64). */
function TickReasonDialog({
  item,
  done,
  onClose,
  onSaved,
}: {
  item: MeetingItem;
  /** True to mark it done, false to reopen it. */
  done: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const initial = t(done ? 'meetingsOverview.reasonDone' : 'meetingsOverview.reasonReopened');
  const [reason, setReason] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [reasonError, setReasonError] = useState(false);
  const [saving, setSaving] = useState(false);
  const guard = useDialogDirtyGuard(reason !== initial, onClose, saving);

  async function handleSave() {
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setReasonError(true);
      setError(t('errors.checkFields'));
      guard.focusErrors();
      return;
    }
    setSaving(true);
    setError(null);
    setReasonError(false);
    try {
      await meetingsApi.patchItem(
        item.virtualAccountId,
        item.meetingId,
        item.occurrenceKey,
        item.id,
        {
          status: done ? 'DONE' : 'OPEN',
          reason: trimmed,
        },
      );
      onSaved();
    } catch {
      setError(t('meetings.errorGeneric'));
      guard.focusErrors();
    } finally {
      setSaving(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId="commitment-tick"
      subtitle={<DialogSubtitle id="commitment-tick-body">{item.body}</DialogSubtitle>}
      subtitleId="commitment-tick-body"
      fullWidth
      maxWidth="sm"
      sx={{ '& .MuiDialog-paper': { maxWidth: 520 } }}
    >
      <DialogTitle id="commitment-tick">
        {t(done ? 'meetingsOverview.doneTitle' : 'meetingsOverview.reopenTitle')}
      </DialogTitle>
      <DialogContent>
        {error !== null && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          label={t('items.reasonLabel')}
          value={reason}
          onChange={(event) => setReason(event.target.value.slice(0, REASON_MAX))}
          error={reasonError}
          helperText={reasonError ? t('items.reasonRequired') : t('items.versionNote')}
          required
          fullWidth
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={guard.requestClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <SubmitButton onClick={() => void handleSave()} loading={saving} disabled={saving}>
          {t('common.save')}
        </SubmitButton>
      </DialogActions>
    </GuardedDialog>
  );
}

interface MeetingsOverviewProps {
  /** The page's occurrences ahead, ascending, and behind, newest first (`splitMeetings`). */
  upcoming: readonly MeetingCalendarEntry[];
  past: readonly MeetingCalendarEntry[];
  /** The page's one items refresh (TMMAPP-0154 item 18): a change reloads the cards. */
  reloadToken?: number;
  /** A tick here changed the items: the page refreshes the cards and the Items tab together. */
  onItemsChanged?: () => void;
}

export function MeetingsOverview({
  upcoming,
  past,
  reloadToken = 0,
  onItemsChanged,
}: MeetingsOverviewProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const notify = useNotify();
  const tokens = resolveTokens(useTheme());
  const label = tokens.textLabel('surface');
  const zone = me.user.timezone ?? 'UTC';
  const [items, setItems] = useState<MeetingItem[] | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [ticking, setTicking] = useState<{ item: MeetingItem; done: boolean } | null>(null);
  const started = useRef(false);

  const load = useCallback(async () => {
    try {
      setItems(await meetingsApi.listMyItems());
      setFailure(null);
    } catch (error) {
      setFailure({ error });
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  const seenToken = useRef(reloadToken);
  useEffect(() => {
    if (seenToken.current === reloadToken) return;
    seenToken.current = reloadToken;
    void load();
  }, [reloadToken, load]);

  const standing = (virtualAccountId: number | null) =>
    virtualAccountId === null
      ? undefined
      : me.virtualAccounts.find((row) => row.id === virtualAccountId);
  // A standalone meeting's rows, by meeting: its "With {names}" and whether the viewer organizes it.
  const standaloneRows = new Map(
    [...upcoming, ...past]
      .filter((entry) => entry.virtualAccountId === null)
      .map((entry) => [entry.meetingId, entry] as const),
  );
  // An item whose meeting is outside the page's ±12-month rows reads its meeting, as
  // `MeetingsItemsTab` does (TMMAPP-0160 item 13); `null` when that read failed.
  const farIds = [
    ...new Set(
      (items ?? [])
        .filter((item) => item.virtualAccountId === null && !standaloneRows.has(item.meetingId))
        .map((item) => item.meetingId),
    ),
  ]
    .sort((a, b) => a - b)
    .join(',');
  const [farMeetings, setFarMeetings] = useState<ReadonlyMap<number, Meeting | null>>(new Map());
  const farRequested = useRef(new Set<number>());
  useEffect(() => {
    const ids = farIds
      .split(',')
      .filter((id) => id.length > 0)
      .map(Number)
      .filter((id) => !farRequested.current.has(id));
    for (const id of ids) {
      farRequested.current.add(id);
      void meetingsApi
        .get(null, id)
        .catch(() => null)
        .then((meeting) => setFarMeetings((prev) => new Map(prev).set(id, meeting)));
    }
  }, [farIds]);
  const withLabelOf = (meetingId: number) => {
    const entry = standaloneRows.get(meetingId);
    if (entry !== undefined) return entryWithLabel(entry, me.user.id, t, locale);
    const meeting = farMeetings.get(meetingId);
    return meeting == null ? null : meetingWithLabel(meeting, me.user.id, t, locale);
  };
  const writes = (item: MeetingItem) => {
    if (item.virtualAccountId !== null) return canWriteNotes(standing(item.virtualAccountId));
    const entry = standaloneRows.get(item.meetingId);
    const meeting = farMeetings.get(item.meetingId);
    const organizer =
      entry !== undefined
        ? organizesEntry(entry, me.user.id)
        : meeting != null && me.user.id !== null && meeting.organizer.userId === me.user.id;
    return canWriteStandalone(me, organizer);
  };
  // Where an item names its envelope, a standalone meeting's item names its people.
  const placeOf = (item: MeetingItem) =>
    item.virtualAccountId === null ? withLabelOf(item.meetingId) : item.envelopeName;
  // "Oct 25" in the viewer's current year, with the year otherwise (TMMAPP-0155 item 8).
  const today = todayCivilInZone(zone);
  const shortDate = (civil: string) => formatCivilDateNear(civil, locale, today);
  // An instant's day in the viewer's zone, read the same way.
  const instantDate = (iso: string) => shortDate(utcToZonedCivil(iso, zone).date);
  const meta = (parts: readonly (string | null)[]) =>
    parts.filter((part): part is string => part !== null && part.length > 0).join(' · ');

  const next = upcoming.find((entry) => entry.status === 'SCHEDULED') ?? null;
  const all = (items ?? []).filter((item) => !item.pendingConfirmation);
  const commitments = all.filter(
    (item) => item.kind === 'COMMITMENT' && (item.status === 'OPEN' || item.status === 'DONE'),
  );
  const decisions = all.filter(
    (item) => item.kind === 'DECISION' && (item.status === 'AGREED' || item.status === 'REVISED'),
  );
  const goals = all.filter((item) => item.kind === 'GOAL' && item.status === 'OPEN');
  const pastShown = past.slice(0, PAST_SHOWN);

  function itemLink(item: MeetingItem, text: ReactNode, sx: object = {}) {
    const to =
      item.kind === 'GOAL' && item.envelopeId !== null && item.virtualAccountId !== null
        ? `/v/${item.virtualAccountId}/e/${item.envelopeId}`
        : sessionPath(item.virtualAccountId, item.meetingId, item.occurrenceKey);
    return (
      <Link
        component={RouterLink}
        to={to}
        underline="hover"
        sx={{
          color: tokens.text,
          fontSize: 14.5,
          lineHeight: '21px',
          whiteSpace: 'pre-wrap',
          ...sx,
        }}
      >
        {text}
      </Link>
    );
  }

  const commitmentRows = commitments.map((item) => {
    const done = item.status === 'DONE';
    const writer = writes(item);
    const textId = `commitment-${item.id}`;
    const box = (
      <Box
        component="span"
        sx={{
          width: 18,
          height: 18,
          borderRadius: '5px',
          border: `1.5px solid ${tokens.brand}`,
          bgcolor: done ? tokens.brand : 'transparent',
          color: tokens.brandInk,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& svg': { width: 13, height: 13 },
        }}
      >
        {done && <CheckIcon />}
      </Box>
    );
    return (
      <Box
        component="li"
        key={item.id}
        data-commitment={item.id}
        sx={{ ...ROW_SX, borderColor: tokens.line, display: 'flex', gap: '12px' }}
      >
        <Box sx={{ flex: '0 0 18px', mt: '2px' }}>
          {writer ? (
            <Checkbox
              checked={done}
              onChange={(event) => setTicking({ item, done: event.target.checked })}
              icon={box}
              checkedIcon={box}
              disableRipple
              slotProps={{ input: { 'aria-labelledby': textId } }}
              sx={{
                p: 0,
                '&.Mui-focusVisible > span': {
                  outline: `2px solid ${tokens.focusRing}`,
                  outlineOffset: 2,
                },
              }}
            />
          ) : (
            <Box
              component="span"
              role="img"
              aria-label={statusLabel(t, 'meetingItem', done ? 'DONE' : 'OPEN')}
              sx={{ display: 'flex' }}
            >
              {box}
            </Box>
          )}
        </Box>
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {itemLink(
            item,
            <span id={textId}>{item.body}</span>,
            done ? { textDecoration: 'line-through' } : {},
          )}
          <Typography component="span" sx={{ fontSize: 12.5, lineHeight: '18px', color: label }}>
            {meta([
              item.ownerUserId === null
                ? t('meetingsOverview.everyone')
                : personName(item.ownerDisplayName, t),
              item.dueOn !== null
                ? t('meetingsOverview.due', { date: shortDate(item.dueOn) })
                : null,
              placeOf(item),
            ])}
          </Typography>
        </Box>
      </Box>
    );
  });

  const decisionRows = decisions.map((item) => (
    <Box
      component="li"
      key={item.id}
      data-decision={item.id}
      sx={{
        ...ROW_SX,
        borderColor: tokens.line,
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
      }}
    >
      <Box sx={{ display: 'flex' }}>
        <TintChip
          tint={item.status === 'REVISED' ? 'peach' : 'sage'}
          label={statusLabel(t, 'meetingItem', item.status ?? 'AGREED')}
        />
      </Box>
      {itemLink(item, item.body)}
      <Typography component="span" sx={{ fontSize: 12.5, lineHeight: '18px', color: label }}>
        {meta([
          t('meetingsOverview.version', { version: item.version }),
          item.lastActorDisplayName,
          instantDate(item.updatedAt),
          placeOf(item),
        ])}
      </Typography>
    </Box>
  ));

  const goalRows = goals.map((item) => {
    const currency = standing(item.virtualAccountId)?.currency ?? null;
    const progress = item.progressMinor;
    const target = item.targetMinor;
    const share =
      progress !== null && target !== null && target > 0 ? Math.max(0, progress / target) : null;
    let line: ReactNode = null;
    if (currency !== null && target !== null) {
      line =
        progress !== null && share !== null ? (
          // The goal's balance is a balance: `moneyNegative` below zero (TMMAPP-0154 item 19).
          <Trans
            i18nKey="meetingsOverview.goalLine"
            values={{
              pct: formatPercent(Math.floor(share * 100) / 100, locale, {
                maximumFractionDigits: 0,
              }),
            }}
            components={{
              balance: (
                <Box
                  component="span"
                  data-goal-balance=""
                  sx={progress < 0 ? { color: tokens.moneyNegative } : {}}
                >
                  <MoneyParts minor={progress} currency={currency} whole />
                </Box>
              ),
              target: <MoneyParts minor={target} currency={currency} whole />,
            }}
          />
        ) : (
          t('meetingsOverview.goalTarget', {
            target: formatMoneyMinor(target, currency, locale, false, { whole: true }),
          })
        );
    }
    return (
      <Box
        component="li"
        key={item.id}
        data-goal={item.id}
        sx={{
          ...ROW_SX,
          borderColor: tokens.line,
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        }}
      >
        {itemLink(item, item.body)}
        {share !== null && (
          // A filled `hover` track, not an outline (TMMAPP-0154 item 19, 0156 item 4).
          <ProgressTrack
            value={share * 100}
            label={t('meetingsOverview.goalBar', { name: item.body })}
          />
        )}
        {line !== null && (
          <Typography
            component="span"
            data-goal-line=""
            sx={{
              fontSize: 12.5,
              lineHeight: '18px',
              color: label,
              fontVariantNumeric: 'tabular-nums',
              overflowWrap: 'anywhere',
            }}
          >
            {line}
          </Typography>
        )}
      </Box>
    );
  });

  const pastRows = pastShown.map((entry) => (
    <Box
      component="li"
      key={`${entry.meetingId}-${entry.occurrenceKey}`}
      data-past-meeting=""
      sx={{
        ...ROW_SX,
        borderColor: tokens.line,
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
      }}
    >
      <Typography component="span" sx={{ fontSize: 12.5, lineHeight: '18px', color: label }}>
        {meta([
          instantDate(entry.startsAt),
          entry.virtualAccountId === null
            ? entryWithLabel(entry, me.user.id, t, locale)
            : (standing(entry.virtualAccountId)?.name ?? null),
        ])}
      </Typography>
      <Link
        component={RouterLink}
        to={sessionPath(entry.virtualAccountId, entry.meetingId, entry.occurrenceKey)}
        underline="hover"
        sx={{ color: tokens.text, fontWeight: 600 }}
      >
        {entry.title}
      </Link>
      {entry.latestNoteExcerpt !== null && (
        <Typography component="span" sx={{ fontSize: 14, lineHeight: '20px', color: tokens.text2 }}>
          {entry.latestNoteExcerpt}
        </Typography>
      )}
    </Box>
  ));

  return (
    <Box
      data-meetings-overview=""
      sx={{ display: 'flex', flexDirection: 'column', gap: '24px', mb: 3 }}
    >
      {next !== null && (
        <ButtonBase
          component={RouterLink}
          to={meetingSessionPath(next)}
          data-next-meeting=""
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '14px',
            p: '16px 20px',
            borderRadius: '18px',
            // The prototype's warm wash (#f6e3d3) with its dark ink (TMMAPP-0154 item 23): `text` on
            // the peach categorical soft, the same wash, where saas-auth guarantees `text` (it
            // doesn't on `warningSoft`).
            bgcolor: tintFor('peach', tokens).soft,
            color: tokens.text,
            textAlign: 'start',
            '&:hover [data-prepare]': { textDecoration: 'underline' },
            '&.Mui-focusVisible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
          }}
        >
          <Box component="span" sx={{ display: 'flex' }} aria-hidden>
            <MeetingsIcon />
          </Box>
          <Typography
            component="span"
            sx={{ flex: 1, fontSize: 14, lineHeight: '20px', fontWeight: 600, minWidth: 0 }}
          >
            {t('meetingsOverview.next', {
              title: next.title,
              date: formatInstant(next.startsAt, locale, zone, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              }),
              time: formatInstant(next.startsAt, locale, zone, { timeStyle: 'short' }),
            })}
          </Typography>
          <Typography component="span" data-prepare="" sx={{ fontWeight: 600, fontSize: 13 }}>
            {t('meetingsOverview.prepare')}
          </Typography>
        </ButtonBase>
      )}
      {failure !== null && (
        <ErrorState
          variant="section"
          error={failure.error}
          what={t('errors.what.meetings')}
          onRetry={() => void load()}
        />
      )}
      {(commitmentRows.length > 0 ||
        decisionRows.length > 0 ||
        goalRows.length > 0 ||
        pastRows.length > 0) && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
            gap: '16px',
            alignItems: 'start',
          }}
        >
          {commitmentRows.length > 0 && (
            <OverviewCard title={t('meetingsOverview.commitments')}>{commitmentRows}</OverviewCard>
          )}
          {decisionRows.length > 0 && (
            <OverviewCard title={t('meetingsOverview.decisions')}>{decisionRows}</OverviewCard>
          )}
          {goalRows.length > 0 && (
            <OverviewCard title={t('meetingsOverview.goals')}>{goalRows}</OverviewCard>
          )}
          {pastRows.length > 0 && (
            <OverviewCard title={t('meetingsOverview.past')}>{pastRows}</OverviewCard>
          )}
        </Box>
      )}
      {ticking !== null && (
        <TickReasonDialog
          item={ticking.item}
          done={ticking.done}
          onClose={() => setTicking(null)}
          onSaved={() => {
            notify(
              t(ticking.done ? 'meetingsOverview.doneSaved' : 'meetingsOverview.reopenedSaved'),
            );
            setTicking(null);
            if (onItemsChanged !== undefined) onItemsChanged();
            else void load();
          }}
        />
      )}
    </Box>
  );
}
